import * as ft from 'funtypes'
import { IsoDateTime, NonEmptyString, objectWith, validate } from '../validation.js'
import type { OcmClient, OcmQuery } from './client.js'
import { type OcmPoi, OcmPoiFile } from './types.js'

/**
 * The raw Open Charge Map dump, cached in Cloud Storage under `raw/ocm/`.
 *
 * Two things this buys, in order of importance:
 *
 *  1. a re-run does not re-download. OCM rate limits per key and a national
 *     dump is one large response; a pipeline that failed at the publish step
 *     should not pay for it twice.
 *  2. the exact bytes the matcher saw are kept, so a surprising match can be
 *     reproduced later against the same input.
 *
 * The refresh path is incremental: with a cached dump in hand the client is
 * called with `modifiedsince=<cached fetchedAt>` and the result is merged by
 * POI id, so an ordinary run transfers the day's edits rather than the country.
 *
 * `@ev/shared` ships to the browser too, so no Google Cloud library is imported
 * here. {@link ObjectStore} is a two-method port;
 * `createBucketObjectStore(getStorage().bucket(name))` fills it on the pipeline
 * side, and {@link createMemoryObjectStore} fills it in tests.
 */

/** Everything raw lives under `raw/`; OCM's corner of it is `raw/ocm/`. */
export const RAW_OCM_PREFIX = 'raw/ocm'

/** How old a cached dump may be before a run refreshes it. */
export const DEFAULT_OCM_CACHE_MAX_AGE_MS = 6 * 60 * 60 * 1000

/** The pointer object a run reads first: `raw/ocm/PL/latest.json`. */
export const ocmDumpPath = (countryCode: string): string =>
  `${RAW_OCM_PREFIX}/${countryCode.toUpperCase()}/latest.json`

/** The dated copy kept beside it: `raw/ocm/PL/2026-08-15.json`. */
export const ocmDumpSnapshotPath = (countryCode: string, fetchedAt: string): string =>
  `${RAW_OCM_PREFIX}/${countryCode.toUpperCase()}/${fetchedAt.slice(0, 10)}.json`

/**
 * The cache envelope. OCM publishes a bare array, so the envelope is ours: it
 * records when the dump was taken, which is what the next incremental refresh
 * passes as `modifiedsince`.
 */
export const OcmDump = objectWith(
  {
    fetchedAt: IsoDateTime,
    countryCode: NonEmptyString,
    pois: OcmPoiFile,
  },
  {
    /** Denormalised for cheap logging; `pois.length` is the truth. */
    poiCount: ft.Number,
  },
)
export type OcmDump = ft.Static<typeof OcmDump>

// ---------------------------------------------------------------------------
// Storage port
// ---------------------------------------------------------------------------

export interface ObjectStore {
  /** `undefined` when the object does not exist. Anything else throws. */
  read(path: string): Promise<string | undefined>
  write(path: string, body: string, contentType?: string): Promise<void>
}

/** The subset of `@google-cloud/storage`'s `File` this needs. */
export interface StorageFileLike {
  download(): Promise<readonly [Uint8Array | string, ...unknown[]]>
  save(data: string, options?: { contentType?: string }): Promise<unknown>
  exists(): Promise<readonly [boolean, ...unknown[]]>
}

/** The subset of `@google-cloud/storage`'s `Bucket` this needs. */
export interface StorageBucketLike {
  file(path: string): StorageFileLike
}

/**
 * Adapt a Cloud Storage bucket to {@link ObjectStore}.
 *
 * ```ts
 * const store = createBucketObjectStore(getStorage().bucket(ARTIFACTS_BUCKET.value()))
 * ```
 */
export const createBucketObjectStore = (bucket: StorageBucketLike): ObjectStore => ({
  read: async (path) => {
    const file = bucket.file(path)
    const [exists] = await file.exists()
    if (!exists) return undefined

    const [contents] = await file.download()
    return typeof contents === 'string' ? contents : new TextDecoder().decode(contents)
  },
  write: async (path, body, contentType) => {
    await bucket.file(path).save(body, { contentType: contentType ?? 'application/json' })
  },
})

/** An in-memory store, for tests and for a dry run with no bucket. */
export const createMemoryObjectStore = (
  seed: Readonly<Record<string, string>> = {},
): ObjectStore & { readonly objects: Map<string, string> } => {
  const objects = new Map<string, string>(Object.entries(seed))
  return {
    objects,
    read: (path) => Promise.resolve(objects.get(path)),
    write: (path, body) => {
      objects.set(path, body)
      return Promise.resolve()
    },
  }
}

// ---------------------------------------------------------------------------
// Read / write
// ---------------------------------------------------------------------------

export const readOcmDump = async (
  store: ObjectStore,
  countryCode: string,
  onWarning?: (message: string) => void,
): Promise<OcmDump | undefined> => {
  const path = ocmDumpPath(countryCode)
  const body = await store.read(path)
  if (body === undefined) return undefined

  let parsed: unknown
  try {
    parsed = JSON.parse(body) as unknown
  } catch (error) {
    onWarning?.(`${path} is not valid JSON (${String(error)}); refetching`)
    return undefined
  }

  const result = validate(OcmDump, parsed, { source: path })
  if (result.ok) return result.value

  // A cache that no longer parses is a cache miss, never a failed run.
  onWarning?.(`${path} did not validate (${result.error}); refetching`)
  return undefined
}

export const writeOcmDump = async (store: ObjectStore, dump: OcmDump): Promise<void> => {
  const body = JSON.stringify({ ...dump, poiCount: dump.pois.length })
  await store.write(ocmDumpPath(dump.countryCode), body)
  await store.write(ocmDumpSnapshotPath(dump.countryCode, dump.fetchedAt), body)
}

// ---------------------------------------------------------------------------
// The cached fetch
// ---------------------------------------------------------------------------

/** What a cached fetch actually did, for the run report. */
export type OcmRefreshKind = 'cache-hit' | 'incremental' | 'full'

export interface CachedOcmFetchOptions {
  readonly client: OcmClient
  readonly store: ObjectStore
  /** Defaults to `PL`. */
  readonly countryCode?: string
  /** Defaults to {@link DEFAULT_OCM_CACHE_MAX_AGE_MS}. */
  readonly maxAgeMs?: number
  /** Ignore the cached dump and re-download the country. */
  readonly force?: boolean
  /** Passed through to the client (page size, limit, abort signal). */
  readonly query?: Omit<OcmQuery, 'countryCode' | 'modifiedSince'>
  /** Injected for testability; defaults to `Date.now`. */
  readonly now?: () => number
  readonly onWarning?: (message: string) => void
}

export interface CachedOcmFetchResult {
  readonly pois: OcmPoi[]
  readonly fetchedAt: string
  readonly refresh: OcmRefreshKind
  /** POIs that came back from OCM on this run, cache hits excluded. */
  readonly downloaded: number
}

/**
 * The dump, from the cache when it is fresh enough and from OCM when it is not.
 *
 * A stale cache is refreshed incrementally: only POIs modified since the cached
 * `fetchedAt` are requested, and they replace their older copies by id. An
 * absent or unreadable cache falls back to a full country download.
 */
export const fetchOcmPoisCached = async (
  options: CachedOcmFetchOptions,
): Promise<CachedOcmFetchResult> => {
  const countryCode = (options.countryCode ?? 'PL').toUpperCase()
  const now = options.now ?? Date.now
  const maxAgeMs = options.maxAgeMs ?? DEFAULT_OCM_CACHE_MAX_AGE_MS
  const cached =
    options.force === true
      ? undefined
      : await readOcmDump(options.store, countryCode, options.onWarning)

  if (cached !== undefined) {
    const age = now() - Date.parse(cached.fetchedAt)
    if (Number.isFinite(age) && age >= 0 && age <= maxAgeMs)
      return {
        pois: cached.pois,
        fetchedAt: cached.fetchedAt,
        refresh: 'cache-hit',
        downloaded: 0,
      }
  }

  const fetchedAt = new Date(now()).toISOString()
  const changed = await options.client.fetchPois({
    ...options.query,
    countryCode,
    ...(cached === undefined ? {} : { modifiedSince: cached.fetchedAt }),
  })

  const merged = new Map<number, OcmPoi>((cached?.pois ?? []).map((poi) => [poi.ID, poi]))
  changed.forEach((poi) => {
    merged.set(poi.ID, poi)
  })
  const pois = [...merged.values()]

  await writeOcmDump(options.store, { fetchedAt, countryCode, pois, poiCount: pois.length })

  return {
    pois,
    fetchedAt,
    refresh: cached === undefined ? 'full' : 'incremental',
    downloaded: changed.length,
  }
}
